/**
 * @typedef {object} UsPhoneChangeInfo
 * @property {string} display
 * @property {boolean} isComplete
 * @property {string} nationalDigits
 */

const US_E164_REGEX = /^\+1\d{10}$/;

/**
 * Validates a 10-digit US national number against basic NANP rules.
 * @param {string} digits Exactly 10 US national digits.
 * @returns {boolean}
 */
function isValidUsNationalDigits(digits) {
  if (typeof digits !== "string" || !/^\d{10}$/.test(digits)) return false;
  if (/^(\d)\1{9}$/.test(digits)) return false;

  const npa = digits.slice(0, 3);
  const nxx = digits.slice(3, 6);

  if (npa[0] < "2" || (npa[1] === "1" && npa[2] === "1")) return false;
  if (nxx[0] < "2" || (nxx[1] === "1" && nxx[2] === "1")) return false;

  return true;
}

/**
 * @param {unknown} input
 * @returns {string} Up to 10 US national digits (no country code).
 */
export function extractNationalDigits(input) {
  const raw = String(input ?? "").trim();
  if (!raw) return "";

  if (raw.startsWith("+")) {
    if (!raw.startsWith("+1")) return "";
    return raw.replace(/\D/g, "").slice(1, 11);
  }

  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

/**
 * @param {unknown} input
 * @returns {string} Display format: (555) 123-4567
 */
export function formatUsPhoneDisplay(input) {
  const d = extractNationalDigits(input);
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Derive national digits after an edit, including backspace through `(`, `)`, ` `, or `-`.
 * @param {string} previousDigits
 * @param {unknown} rawInput
 * @returns {string}
 */
export function resolveNationalDigitsFromInput(previousDigits, rawInput) {
  const prev = String(previousDigits ?? "").slice(0, 10);
  const next = extractNationalDigits(rawInput);

  if (next.length !== prev.length) {
    return next;
  }

  const formattedPrev = formatUsPhoneDisplay(prev);
  if (String(rawInput ?? "").length < formattedPrev.length) {
    return prev.slice(0, -1);
  }

  return next;
}

/**
 * @param {unknown} input
 * @returns {string|null} +15551234567 when exactly 10 national digits; otherwise null.
 */
export function toUsE164(input) {
  const d = extractNationalDigits(input);
  return d.length === 10 && isValidUsNationalDigits(d) ? `+1${d}` : null;
}

/**
 * @param {unknown} input
 * @returns {boolean}
 */
export function isCompleteUsPhone(input) {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) return false;
  if (US_E164_REGEX.test(trimmed)) {
    return isValidUsNationalDigits(trimmed.slice(2));
  }
  const d = extractNationalDigits(trimmed);
  return d.length === 10 && isValidUsNationalDigits(d);
}

/**
 * @param {unknown} value
 * @returns {string|null} Canonical +1XXXXXXXXXX or null if not valid.
 */
export function assertUsE164(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  if (US_E164_REGEX.test(trimmed)) {
    return isValidUsNationalDigits(trimmed.slice(2)) ? trimmed : null;
  }
  return toUsE164(trimmed);
}

/**
 * Normalize for GraphQL/API payloads. Empty input returns null.
 * @param {unknown} input
 * @returns {string|null}
 */
export function normalizePhoneForBackend(input) {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) return null;
  return assertUsE164(trimmed);
}

/**
 * @param {unknown[]} values
 * @returns {string[]}
 */
export function normalizeUsPhoneList(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((entry) => normalizePhoneForBackend(entry))
    .filter(Boolean);
}

export const INVALID_US_PHONE_MESSAGE = "Please enter a valid US phone number.";

/**
 * @param {unknown} value
 * @returns {string}
 */
export function validateUsPhoneRequired(value) {
  if (!String(value ?? "").trim()) return "Phone number is required.";
  return isCompleteUsPhone(value) ? "" : INVALID_US_PHONE_MESSAGE;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function validateUsPhoneOptional(value) {
  if (!String(value ?? "").trim()) return "";
  return isCompleteUsPhone(value) ? "" : INVALID_US_PHONE_MESSAGE;
}

/**
 * @param {string} value
 * @returns {number[]}
 */
function digitCharIndexes(value) {
  const indexes = [];
  for (let i = 0; i < value.length; i += 1) {
    if (/\d/.test(value[i])) indexes.push(i);
  }
  return indexes;
}

/**
 * @param {string} value
 * @param {number} digitStart
 * @param {number} digitEnd
 * @returns {{ start: number, end: number }|null}
 */
function rangeFromDigitSlice(value, digitStart, digitEnd) {
  const indexes = digitCharIndexes(value);
  if (!indexes.length) return null;
  const safeStart = Math.max(0, Math.min(digitStart, indexes.length - 1));
  const safeEnd = Math.max(safeStart + 1, Math.min(digitEnd, indexes.length));
  return { start: indexes[safeStart], end: indexes[safeEnd - 1] + 1 };
}

/**
 * @param {unknown} input
 * @returns {{ start: number, end: number }|null}
 */
export function getInvalidUsPhoneRange(input) {
  const value = String(input ?? "").trim();
  if (!value) return null;

  const invalidCharMatch = value.match(/[^\d()+\-\s]/);
  if (invalidCharMatch?.index != null) {
    const idx = invalidCharMatch.index;
    return { start: idx, end: idx + 1 };
  }

  if (value.startsWith("+") && !value.startsWith("+1")) {
    return { start: 0, end: Math.min(2, value.length) };
  }

  const rawDigits = value.replace(/\D/g, "");
  if (!rawDigits) return { start: 0, end: value.length };

  if (rawDigits.length > 11) {
    return rangeFromDigitSlice(value, 11, rawDigits.length) ?? { start: 0, end: value.length };
  }

  if (rawDigits.length === 11 && !rawDigits.startsWith("1")) {
    return rangeFromDigitSlice(value, 0, 1) ?? { start: 0, end: value.length };
  }

  const nationalDigits = rawDigits.length === 11 && rawDigits.startsWith("1") ? rawDigits.slice(1) : rawDigits;

  if (nationalDigits.length < 10) {
    return rangeFromDigitSlice(value, 0, nationalDigits.length) ?? { start: 0, end: value.length };
  }

  if (/^(\d)\1{9}$/.test(nationalDigits)) {
    return rangeFromDigitSlice(value, rawDigits.length === 11 ? 1 : 0, rawDigits.length === 11 ? 11 : 10);
  }

  const npa = nationalDigits.slice(0, 3);
  const nxx = nationalDigits.slice(3, 6);
  const offset = rawDigits.length === 11 ? 1 : 0;

  if (npa[0] < "2") {
    return rangeFromDigitSlice(value, offset, offset + 1);
  }
  if (npa[1] === "1" && npa[2] === "1") {
    return rangeFromDigitSlice(value, offset + 1, offset + 3);
  }
  if (nxx[0] < "2") {
    return rangeFromDigitSlice(value, offset + 3, offset + 4);
  }
  if (nxx[1] === "1" && nxx[2] === "1") {
    return rangeFromDigitSlice(value, offset + 4, offset + 6);
  }

  return null;
}

/**
 * @param {unknown} input
 * @returns {string}
 */
export function getUsPhoneValidationHint(input) {
  const value = String(input ?? "").trim();
  if (!value) return "";

  const invalidCharMatch = value.match(/[^\d()+\-\s]/);
  if (invalidCharMatch) {
    return "Use numbers only (plus optional +1).";
  }

  if (value.startsWith("+") && !value.startsWith("+1")) {
    return "Use +1 for US phone numbers.";
  }

  const rawDigits = value.replace(/\D/g, "");
  if (!rawDigits) return "Include a 10-digit US number.";
  if (rawDigits.length > 11) return "Remove extra digits.";
  if (rawDigits.length === 11 && !rawDigits.startsWith("1")) return "US numbers can only use leading country code 1.";

  const nationalDigits = rawDigits.length === 11 && rawDigits.startsWith("1") ? rawDigits.slice(1) : rawDigits;
  if (nationalDigits.length < 10) {
    const missingCount = 10 - nationalDigits.length;
    return `Missing ${missingCount} digit${missingCount === 1 ? "" : "s"}.`;
  }
  if (!isValidUsNationalDigits(nationalDigits)) {
    return "Check area code and prefix.";
  }

  return "";
}
